import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { Resource, User } from '../../utils/interfaces';
import { copy, deleteFromBucket, getPrettyDate, randString, uploadToBucket } from '../../utils/functions';
import { config } from '../../config';
import { databaseName, defaultPluginCode, pluginYml } from '../../utils/constants';
import Dropdown from '../global/Dropdown';
import { useEventListener } from '@iwbam/react-ez';
import Popup from '../global/Popup';

const Home = ({ user, setUser }: { user: undefined | null | User, setUser: React.Dispatch<React.SetStateAction<undefined | null | User>> }) => {

  const navigate = useNavigate();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);

  // add resource
  const [resourceName, setResourceName] = useState('');
  const [isAddingResource, setIsAddingResource] = useState(false);
  // search & sort
  const [sortBy, setSortBy] = useState('Created');
  const [isDescending, setIsDescending] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  // options
  const [optionOpening, setOptionOpening] = useState('');
  const [resourceRenaming, setResourceRenaming] = useState('');
  const [renameInput, setRenameInput] = useState('');
  const [resourceDeleting, setResourceDeleting] = useState('');

  useEffect(() => setOptionOpening(''), [resourceRenaming, resourceDeleting]);

  // sort resources
  useEffect(() => {
    if (sortBy === 'Created') {
      setResources(copy(resources).sort((a: Resource, b: Resource) => (a.created < b.created ? -1 : 1) * (isDescending ? -1 : 1)));
    } else if (sortBy === 'Last updated') {
      setResources(copy(resources).sort((a: Resource, b: Resource) => (a.lastUpdated < b.lastUpdated ? -1 : 1) * (isDescending ? -1 : 1)));
    } else if (sortBy === 'Name') {
      setResources(copy(resources).sort((a: Resource, b: Resource) => (a.name === b.name ? (a.created < b.created ? -1 : 1) : a.name < b.name ? -1 : 1) * (isDescending ? -1 : 1)));
    }
  }, [sortBy, loading, isDescending]);

  // load all resources
  useEffect(() => {
    if (user) {
      if (user.username === 'user') {
        setUser(null);
        return;
      }
      (async () => {
        const allRes = await (await fetch(`${config.api.database}?table=${databaseName}_resources&num=multiple&keys=['author']&values=['${user.username}']`)).json();
        setResources(allRes);
        setLoading(false);
      })();
    }
  }, [user]);

  // event listener
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.dropdown-container')) { // close dropdown
      setShowDropdown(false);
    }
    if (!target.closest(`#options-container-${optionOpening}`)) { // close options
      setOptionOpening('');
      if (!target.closest(`.rename-input`)) { // close rename popup
        setResourceRenaming('');
      }
    }
  });

  // add new resource
  const addResource = async () => {
    // create new resource
    const resourceId = randString(12);
    const javaId = `${randString(7)}.java`;
    const ymlId = `${randString(7)}.yml`;
    const fileName = resourceName.trim()[0].toUpperCase() + resourceName.replaceAll(' ', '').slice(1);
    const newResource: Resource = {
      id: resourceId,
      name: resourceName,
      mainClass: fileName,
      version: '1.20',
      description: '',
      author: user?.username || 'user',
      lastUpdated: new Date().toISOString(),
      created: new Date().toISOString(),
      root: {
        id: resourceId,
        name: resourceName,
        items: [{
          id: javaId,
          name: `${fileName}.java`,
        }, {
          id: ymlId,
          name: 'plugin.yml',
        }],
      }
    };
    await fetch(`${config.api.database}`, { method: 'POST', body: JSON.stringify({ table: `${databaseName}_resources`, data: newResource }) });
    await uploadToBucket(`${(user?.username || 'user')}/${resourceId}/${newResource.root.items[0].id}`, `package ${(user?.username || 'user')}.${resourceName.replaceAll(' ', '').toLowerCase()};\n\n` + defaultPluginCode.replaceAll('NewPlugin', fileName));
    await uploadToBucket(`${(user?.username || 'user')}/${resourceId}/${newResource.root.items[1].id}`, pluginYml.replace('<USERNAME>', (user?.username || 'user')).replace('<RESOURCE_NAME>', resourceName.replaceAll(' ', '').toLowerCase()).replace('Author', user?.username || 'user').replaceAll('NewPlugin', fileName));
    await uploadToBucket(`${(user?.username || 'user')}/${resourceId}/manifest.txt`, `Main-Class: ${fileName}`);
    // navigate to new resource page
    navigate(`/resources/${newResource.id}`);
  }

  // rename resource
  const renameResource = async () => {
    if (renameInput.trim().length === 0) {
      setResourceRenaming('');
      return;
    }
    setResources(prev => prev.map(resource => resource.id !== resourceRenaming ? resource : { ...resource, name: renameInput }));
    fetch(config.api.database, {
      method: 'PATCH', body: JSON.stringify({
        table: `${databaseName}_resources`,
        item_key: 'id',
        item_key_value: resourceRenaming,
        keys: ['name', 'root'],
        values: [renameInput, { ...resources.find(r => r.id === resourceRenaming)?.root, name: renameInput }]
      })
    });
    setResourceRenaming('');
  }

  // delete resource
  const deleteResource = async () => {
    setResources(prev => prev.filter(resource => resource.id !== resourceDeleting));
    fetch(`${config.api.database}?table=${databaseName}_resources&key=id&value=${resourceDeleting}`, { method: 'DELETE' });
    deleteFromBucket(`${user?.username}/${resourceDeleting}`, true);
    setResourceDeleting('');
    localStorage.setItem('mc-resources', JSON.stringify([...resources.filter(resource => resource.id !== resourceDeleting).map(r => r.id)]));
  }

  // check if name is valid
  const isValidName = (name: string) => {
    return /^[a-zA-Z0-9]+$/.test(name.replaceAll(' ', '')) && /^[a-zA-Z]$/.test(name[0]);
  }

  return (
    <>
      <div className='overflow-hidden flex-1 flex items-center flex-col'>
        <main className='w-full h-full flex flex-col max-w-5xl px-8 scroller'>
          <div className='flex items-center justify-between mt-4 py-2 border-b'>
            <div className='text-2xl font-semibold'>My Resources</div>
            <button className='trans-2 bg-emerald-500 text-sm py-1 px-4 rounded font-semibold active:bg-emerald-600' onClick={() => {
              setIsAddingResource(true);
            }}>Add resource</button>
          </div>
          {resources.length === 0 && (!loading || user === null) ?
            <div className='flex-1 flex flex-col items-center justify-center'>
              <div className='text-lg text-gray-400'>{"You don't have any plugin yet"}</div>
              <button className='mt-4 trans-2 bg-emerald-500 py-1.5 px-8 rounded font-semibold active:bg-emerald-600' onClick={() => {
                setIsAddingResource(true);
              }}>Create a resource</button>
            </div>
            : resources.length > 0 ?
              <>
                {/* search & sort */}
                <div className='flex items-center text-2xs md:text-sm trans-2 py-2 justify-end'>
                  <div className='ml-8'>Sort by:</div>
                  <div className='ml-2 w-32 relative dropdown-container'>
                    <button onClick={() => setShowDropdown(!showDropdown)} className='w-full border rounded flex items-center px-2 h-6 bg-gray-800'>{sortBy}</button>
                    {showDropdown && <Dropdown selected={sortBy} setSelected={setSortBy} setShowDropdown={setShowDropdown} options={['Created', 'Last updated', 'Name']} className='absolute w-full z-10' style={{ top: '110%' }} />}
                  </div>
                  <button className='flex items-center justify-center aspect-square border h-6 rounded ml-4 bg-gray-800' onClick={() => setIsDescending(!isDescending)}><i className={`fa-solid fa-angle-${isDescending ? 'down' : 'up'}`} /></button>
                </div>
                {/* resources list */}
                <table className='mt-4'>
                  <thead className='text-sm text-left'>
                    <tr className='*:py-1 border-b border-gray-600'>
                      <th className='w-1/2'>Title</th>
                      <th>Created</th>
                      <th>Last updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map(resouce => <tr key={resouce.id} className='text-sm text-left *:py-1'>
                      {resourceRenaming === resouce.id ?
                        <td className='flex items-center relative rename-input'>
                          <input className='bg-gray-800 border flex-1 p-1 mr-2 rounded' value={renameInput} onInput={(e: React.ChangeEvent<HTMLInputElement>) => setRenameInput(e.target.value)} />
                          <button onClick={renameResource} className='absolute top-1/2 right-4 p-1 hover:text-emerald-500 -translate-y-1/2'><i className='fa-solid fa-check' /></button>
                        </td>
                        : <td className='cursor-pointer hover:underline hover:text-emerald-400' onClick={() => navigate(`/resources/${resouce.id}`)}>{resouce.name}</td>
                      }
                      <td className='font-light text-xs min-w-24'>{getPrettyDate(resouce.created)}</td>
                      <td className='font-light text-xs min-w-24'>{getPrettyDate(resouce.lastUpdated)}</td>
                      <td>
                        <div className='relative w-6' id={`options-container-${resouce.id}`}>
                          <i onClick={() => setOptionOpening(resouce.id)} className='fa-solid fa-ellipsis cursor-pointer hover:bg-slate-700 w-6 aspect-square flex items-center justify-center rounded-full' />
                          {optionOpening === resouce.id && <div className='text-xs z-10 absolute w-24 bg-gray-800 border overflow-hidden rounded right-0' style={{ top: '110%' }}>
                            <button onClick={() => {
                              setResourceRenaming(resouce.id);
                              setRenameInput(resouce.name);
                            }} className='p-1 px-2 hover:bg-gray-700 w-full text-left'>Rename</button>
                            <button onClick={() => setResourceDeleting(resouce.id)} className='p-1 px-2 hover:bg-gray-700 w-full text-left'>Delete</button>
                          </div>}
                        </div>
                      </td>
                    </tr>)}
                  </tbody>
                </table>
              </>
              : <></>}
        </main>
      </div>

      {/* popup */}

      {resourceDeleting && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md delete-popup'>
          <div className='font-bold'>Are you sure you want to delete this resource?</div>
          <div className='mt-4 font-light'>This action is irreversible.</div>
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={deleteResource} className='font-bold bg-red-600 p-1 px-4 rounded active:bg-red-700'>Confirm delete</button>
            <button onClick={() => setResourceDeleting('')} className='font-bold bg-gray-500 p-1 px-4 rounded ml-4 active:bg-gray-600'>Cancel</button>
          </div>
        </Popup>
      </div>}

      {isAddingResource && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md add-resource-popup'>
          <div className='font-bold'>New resource</div>
          <input autoFocus className='border rounded mt-4 py-1 px-2 text-sm w-full' value={resourceName} onInput={(e: React.ChangeEvent<HTMLInputElement>) => setResourceName(e.target.value)} />
          <div className='text-xs font-light mt-1'>Name should contain only A-Z, a-z, 0-9, _. The fist character should be a letter.</div>
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={addResource} className='font-bold bg-emerald-500 p-1 px-4 rounded active:bg-emerald-600 disabled:bg-gray-400' disabled={!isValidName(resourceName)}>Create</button>
            <button onClick={() => setIsAddingResource(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}

    </>
  )
}

export default Home